/**
 * @module data/site
 *
 * Site-wide content: brand, navigation, hero copy, contact details and the
 * legal/Impressum information.
 *
 * Everything the shell needs lives here, so the HTML stays a structural
 * skeleton and copy edits never require touching markup or components.
 */

/**
 * @typedef {Object} NavItem
 * @property {string} id     Section id / anchor target.
 * @property {string} label  Navigation label.
 */

/** Primary section navigation. Order matches the page order. */
export const NAV = [
  { id: "intro", label: "Method" },
  { id: "adm", label: "ADM" },
  { id: "journey", label: "Journey" },
  { id: "ai-layer", label: "AI Layer" },
  { id: "capabilities", label: "Capabilities" },
  { id: "governance", label: "Governance" },
  { id: "proof", label: "Proof" },
  { id: "insights", label: "Insights" },
  { id: "horizon", label: "Horizon" },
];

/** Brand identity. */
export const BRAND = {
  name: "inmethods",
  /** The two syllables that make up the wordmark, styled separately. */
  wordmark: { lead: "in", tail: "methods" },
  tagline: "Method before motion.",
  description:
    "inmethods is a transformation consultancy that pairs the TOGAF Architecture Development Method with an AI-native delivery model. We redesign business processes, build the capabilities an organisation needs, and govern the change so it compounds.",
  /** Short line used in the header, footer and metadata. */
  promise: "Enterprise architecture and AI, delivered by method.",
};

/** Hero content. */
export const HERO = {
  eyebrow: "Transformation consultancy · TOGAF · Agentic AI",
  titleLines: ["Every transformation", "deserves", "a method."],
  /** The emphasised line index in `titleLines`. */
  accentLine: 2,
  lede:
    "Digital and AI programmes fail for the same reason they always have: motion without method. We run the TOGAF Architecture Development Method, extended with the capabilities, governance and agentic patterns that modern AI demands.",
  primaryCta: { label: "Walk the method", href: "#adm" },
  secondaryCta: { label: "See the AI layer", href: "#ai-layer" },
  meta: [
    { term: "Framework", value: "TOGAF ADM · 9 phases" },
    { term: "Extended by", value: "AI capability & governance" },
    { term: "Engagement", value: "Advisory · Delivery · Enablement" },
  ],
};

/** Trust strip shown under the hero. */
export const HERO_MARKERS = [
  "Business architecture",
  "Data & AI governance",
  "Agentic delivery",
  "Operating model design",
  "Responsible AI",
];

/**
 * Statistics for the introduction section. Kept in one place so the copy and
 * the animated counters cannot drift apart.
 *
 * @typedef {Object} Stat
 * @property {string} value  Numeric target for the count-up.
 * @property {string} suffix Unit appended after the number.
 * @property {string} label  Short label.
 * @property {string} detail Supporting sentence.
 */

/** @type {Stat[]} */
export const STATS = [
  {
    value: "9",
    suffix: "",
    label: "ADM phases",
    detail: "From preliminary framing through architecture change management.",
  },
  {
    value: "4",
    suffix: "",
    label: "Architecture domains",
    detail: "Business, data, application and technology, kept in one line of sight.",
  },
  {
    value: "12",
    suffix: "",
    label: "Capabilities",
    detail: "The parallel disciplines that carry a transformation end to end.",
  },
  {
    value: "100",
    suffix: "%",
    label: "Traceable",
    detail: "Every requirement maps to a capability, a decision and an owner.",
  },
];

/**
 * The three ideas behind the inmethods method.
 *
 * @typedef {Object} Pillar
 * @property {string} id
 * @property {string} title
 * @property {string} body
 * @property {string} glyph  Key into the SVG icon factory.
 */

/** @type {Pillar[]} */
export const PILLARS = [
  {
    id: "adm",
    title: "A method, not a mood",
    body: "The TOGAF ADM gives every decision a place, a sequence and an owner. It turns transformation from a series of opinions into a traceable line of reasoning.",
    glyph: "compass",
  },
  {
    id: "capabilities",
    title: "Capabilities, not projects",
    body: "Projects end; capabilities persist. We design the business capabilities an organisation needs to run itself in an AI-augmented way, then build toward them.",
    glyph: "layers",
  },
  {
    id: "ai-native",
    title: "AI-native by default",
    body: "AI is an architectural concern, not an afterthought. Data, models, agents and guardrails are designed into the target architecture from phase A onward.",
    glyph: "spark",
  },
];

/**
 * The service provider whose details the Impressum must disclose.
 *
 * This is a **sole trader / individual** disclosure: there is no commercial
 * register entry and no legal form to state, so those fields are `null` and the
 * Impressum renders only what actually applies. Optional disclosures must be
 * complete or absent — a bare register number, or a VAT id with no issuer, is
 * worse than saying nothing.
 *
 * @typedef {Object} Company
 * @property {string}      name        Trading name.
 * @property {string}      owner       The natural person responsible for the site.
 * @property {string|null} legalForm   e.g. `"Einzelunternehmen"`; `null` when none.
 * @property {string}      street
 * @property {string}      postalCode
 * @property {string}      city
 * @property {string}      country
 * @property {string}      email       Required contact channel (§ 5 DDG).
 * @property {string|null} phone       The required second, fast contact channel.
 * @property {string|null} vatId       § 27a UStG, when one exists.
 * @property {string|null} register    Register type, when one exists.
 * @property {string|null} registerNumber
 * @property {string|null} supervisoryAuthority
 */

/** @type {Company} */
export const COMPANY = {
  name: "inmethods",
  owner: "Joshua Schütten",
  legalForm: "Freelance consultant (Freiberufler)",
  street: "Parsevalstr. 39",
  postalCode: "45470",
  city: "Mülheim an der Ruhr",
  country: "Germany",
  email: "info@inmethods.com",
  /**
   * `null` until a real number is supplied. § 5 DDG asks for a second, fast
   * contact channel in addition to e-mail, so this is an open gap — publishing
   * an invented number would be worse than showing none.
   */
  phone: null,
  /** No VAT id: small business under § 19 UStG, so nothing to disclose. */
  vatId: null,
  register: null,
  registerNumber: null,
  supervisoryAuthority: null,
};

/**
 * § 18(2) MStV names the person responsible for editorial content. Derived from
 * `COMPANY` so the two can never drift apart.
 *
 * @type {string}
 */
export const RESPONSIBLE_FOR_CONTENT = `${COMPANY.owner}, ${COMPANY.street}, ${COMPANY.postalCode} ${COMPANY.city}`;

/**
 * Contact channels, used by the Impressum so it cannot contradict the footer.
 *
 * Phone is only listed when a real number exists, rather than shipping an
 * invented one.
 *
 * @typedef {Object} ContactDetail
 * @property {string} label
 * @property {string} value
 * @property {string} [href]
 */

/** @type {ContactDetail[]} */
export const CONTACT = [
  { label: "E-mail", value: COMPANY.email, href: `mailto:${COMPANY.email}` },
  ...(COMPANY.phone
    ? [
        {
          label: "Phone",
          value: COMPANY.phone,
          href: `tel:${COMPANY.phone.replace(/[^+\d]/g, "")}`,
        },
      ]
    : []),
];

/** Legal copy for the Impressum page. */
export const LEGAL = {
  disclaimer: `${COMPANY.owner} maintains this website with the greatest possible care. No liability can be accepted for the accuracy, completeness or timeliness of the content, nor for the content of external links. Responsibility for linked pages always rests with their respective operators.`,
  copyright: `The content and works on this website are protected by German copyright law. Contributions by third parties are marked as such. Reproduction, adaptation, distribution or any form of exploitation beyond the limits of copyright law requires the written consent of ${COMPANY.owner}.`,
  privacy: [
    "This website is a static site. It sets no cookies, loads no tracking scripts and stores no personal data on our servers.",
    "Fonts and all assets are served from the same origin. The only outbound requests are the page's own static files.",
    "If you contact us by e-mail, we process the content of your message solely to answer your enquiry, under Art. 6(1)(b) and (f) GDPR. Messages are deleted once the enquiry is resolved and no statutory retention obligations apply.",
    "You have the right to information, rectification, erasure, restriction of processing, data portability and objection. You may also lodge a complaint with a data protection supervisory authority.",
  ],
  trademark:
    "TOGAF® is a registered trademark of The Open Group. ArchiMate® is a registered trademark of The Open Group. All other trademarks are the property of their respective owners. inmethods is an independent consultancy and is not affiliated with or endorsed by The Open Group.",
};

/** Footer structure. */
export const FOOTER = {
  blurb:
    "An independent transformation consultancy. We pair the TOGAF Architecture Development Method with an AI-native delivery model, so that change is designed, governed and finished.",
  columns: [
    {
      title: "The method",
      links: [
        { label: "Methodology", href: "#intro" },
        { label: "The ADM cycle", href: "#adm" },
        { label: "Phase deep-dive", href: "#journey" },
        { label: "AI capability layer", href: "#ai-layer" },
      ],
    },
    {
      title: "What we do",
      links: [
        { label: "Capabilities", href: "#capabilities" },
        { label: "Governance model", href: "#governance" },
        { label: "Case studies", href: "#proof" },
        { label: "Consulting services", href: "#services" },
      ],
    },
    {
      title: "Thinking",
      links: [
        { label: "Insights", href: "#insights" },
        { label: "Trend radar", href: "#horizon" },
        { label: "Impressum", href: "./impressum.html" },
      ],
    },
  ],
};

/**
 * SEO and structured-data copy.
 *
 * The title stays under roughly 60 characters and the description under roughly
 * 155, so search engines render them whole instead of truncating them
 * mid-sentence. The build injects both, plus the generated JSON-LD, into the
 * HTML (see `src/build/seo.js`).
 */
export const SEO = {
  title: "inmethods — Enterprise architecture and AI transformation",
  description:
    "Transformation consultancy pairing the TOGAF ADM with AI-native delivery: nine phases, twelve capabilities, and governance for agentic AI.",
  siteName: "inmethods",
  locale: "en-GB",
};

/**
 * Look up a contact detail by its label.
 *
 * @param {string} label
 * @returns {ContactDetail|undefined}
 */
export function getContact(label) {
  return CONTACT.find((entry) => entry.label.toLowerCase() === label.toLowerCase());
}
